/**
 * Screen Capture Component
 * Uses MediaRecorder API for screenshots and screen recordings
 * 
 * Features:
 * - Screenshot capture
 * - Screen recording with audio option
 * - Preview before upload
 * - Direct integration with IT Tickets
 */

import React, { useState, useRef, useCallback } from 'react';
import {
  Camera,
  Video,
  Square,
  Loader2,
  X,
  Check,
  Monitor,
  Mic,
  MicOff
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';

const ScreenCapture = ({ onCapture, disabled = false }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [capturedMedia, setCapturedMedia] = useState(null);
  const [mediaType, setMediaType] = useState(null); // 'screenshot' or 'recording'
  const [recordingTime, setRecordingTime] = useState(0);
  const [includeAudio, setIncludeAudio] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const captureScreenshot = async () => {
    try {
      // Request screen capture
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          mediaSource: 'screen',
          cursor: 'always'
        }
      });

      // Get video track
      const videoTrack = stream.getVideoTracks()[0];
      const imageCapture = new ImageCapture(videoTrack);

      // Capture frame
      const bitmap = await imageCapture.grabFrame();
      
      // Convert to canvas and then to blob
      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(bitmap, 0, 0);

      // Stop the stream
      stream.getTracks().forEach(track => track.stop());

      // Convert to blob
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      
      const file = new File([blob], `screenshot_${Date.now()}.png`, { type: 'image/png' });
      
      setCapturedMedia({
        file,
        previewUrl: URL.createObjectURL(blob),
        type: 'image'
      });
      setMediaType('screenshot');
      setShowPreview(true);

    } catch (err) {
      if (err.name === 'NotAllowedError') {
        toast.error('Screen capture permission denied');
      } else {
        console.error('Screenshot error:', err);
        toast.error('Failed to capture screenshot');
      }
    }
  };

  const startRecording = async () => {
    try {
      // Request screen capture with optional audio
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          mediaSource: 'screen',
          cursor: 'always'
        },
        audio: includeAudio
      });

      // If audio is enabled but not captured from display, try microphone
      let combinedStream = displayStream;
      if (includeAudio && !displayStream.getAudioTracks().length) {
        try {
          const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          combinedStream = new MediaStream([
            ...displayStream.getVideoTracks(),
            ...audioStream.getAudioTracks()
          ]);
        } catch (audioErr) {
          console.warn('Could not capture audio:', audioErr);
          toast.info('Recording without audio');
        }
      }

      streamRef.current = combinedStream;
      chunksRef.current = [];

      // Create MediaRecorder
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? 'video/webm;codecs=vp9'
        : 'video/webm';

      const mediaRecorder = new MediaRecorder(combinedStream, {
        mimeType,
        videoBitsPerSecond: 2500000 // 2.5 Mbps
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const file = new File([blob], `recording_${Date.now()}.webm`, { type: mimeType });
        
        setCapturedMedia({
          file,
          previewUrl: URL.createObjectURL(blob),
          type: 'video'
        });
        setMediaType('recording');
        setShowPreview(true);

        // Cleanup
        combinedStream.getTracks().forEach(track => track.stop());
        clearInterval(timerRef.current);
        setRecordingTime(0);
      };

      // Handle stream end (user stops sharing)
      combinedStream.getVideoTracks()[0].onended = () => {
        if (mediaRecorderRef.current?.state === 'recording') {
          stopRecording();
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (err) {
      if (err.name === 'NotAllowedError') {
        toast.error('Screen recording permission denied');
      } else {
        console.error('Recording error:', err);
        toast.error('Failed to start recording');
      }
    }
  };

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, []);

  const handleConfirmCapture = () => {
    if (capturedMedia && onCapture) {
      onCapture(capturedMedia.file);
    }
    handleClosePreview();
  };

  const handleClosePreview = () => {
    if (capturedMedia?.previewUrl) {
      URL.revokeObjectURL(capturedMedia.previewUrl);
    }
    setCapturedMedia(null);
    setShowPreview(false);
    setMediaType(null);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Check browser support
  const isSupported = typeof navigator !== 'undefined' && 
    navigator.mediaDevices && 
    typeof navigator.mediaDevices.getDisplayMedia === 'function';

  if (!isSupported) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Camera className="w-4 h-4" />
        <span>Screen capture not supported in this browser</span>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2">
        {/* Screenshot button */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={captureScreenshot}
          disabled={disabled || isRecording}
          title="Take screenshot"
        >
          <Camera className="w-4 h-4 mr-2" />
          Screenshot
        </Button>

        {/* Recording controls */}
        {isRecording ? (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium text-red-700">
                Recording {formatTime(recordingTime)}
              </span>
            </div>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={stopRecording}
            >
              <Square className="w-4 h-4 mr-2" />
              Stop
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={startRecording}
              disabled={disabled}
              title="Start screen recording"
            >
              <Video className="w-4 h-4 mr-2" />
              Record
            </Button>
            <button
              type="button"
              onClick={() => setIncludeAudio(!includeAudio)}
              className={`p-2 rounded-lg border transition-colors ${
                includeAudio 
                  ? 'bg-blue-50 border-blue-200 text-blue-600' 
                  : 'bg-gray-50 border-gray-200 text-gray-400'
              }`}
              title={includeAudio ? 'Audio enabled' : 'Audio disabled'}
            >
              {includeAudio ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
            </button>
          </div>
        )}
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={handleClosePreview}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {mediaType === 'screenshot' ? 'Screenshot Preview' : 'Recording Preview'}
            </DialogTitle>
            <DialogDescription>
              Review your capture before uploading
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4">
            {capturedMedia?.type === 'image' ? (
              <img
                src={capturedMedia.previewUrl}
                alt="Screenshot preview"
                className="w-full rounded-lg border border-gray-200"
              />
            ) : (
              <video
                src={capturedMedia?.previewUrl}
                controls
                className="w-full rounded-lg border border-gray-200"
              />
            )}
          </div>

          <div className="mt-2 text-sm text-gray-500">
            File size: {capturedMedia?.file && (
              (capturedMedia.file.size / (1024 * 1024)).toFixed(2) + ' MB'
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClosePreview}>
              <X className="w-4 h-4 mr-2" />
              Discard
            </Button>
            <Button onClick={handleConfirmCapture}>
              <Check className="w-4 h-4 mr-2" />
              Use This
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ScreenCapture;
